<template>
  <!-- Caso A: logueado, SIN url (formulario de pago) -->
  <div v-if="mostrarUrl === ''" class="loggedin-wrapper" style="max-width:400px;margin: 3rem auto;">

    <!-- PASO 1: Selector de país (antes de mostrar Stripe) -->
    <div v-if="!pais && !bloquearUI" class="mb-4">
      <div class="pais-selector-card">
        <p class="pais-selector-title">{{ $t('pronostico.country') }} <span class="text-danger">*</span></p>
        <select
          v-model="paisSeleccionado"
          class="form-control"
          @change="onPaisChange"
        >
          <option value="" disabled>{{ $t('pronostico.countryPlaceholder') }}</option>
          <option v-for="p in paisesOrdenados" :key="p" :value="p">{{ getCountryLabel(p) }}</option>
        </select>
        <small class="text-muted d-block mt-1" style="font-size:0.78rem;">{{ $t('pronostico.countryHelp') }}</small>
      </div>
    </div>

    <!-- PASO 2: Email + Stripe (tras elegir país) -->
    <template v-if="pais || bloquearUI">
      <!-- Email del usuario (readonly) -->
      <div class="mb-3 text-start">
        <label class="form-label fw-semibold" style="font-size:0.9rem;">
          {{ $t('pronostico.email') }}
        </label>
        <input
          type="email"
          :value="emailLogged"
          class="form-control"
          readonly
          disabled
          style="background:#f8f9fa; color:#495057;"
        />
      </div>

      <!-- Spinner mientras se prepara el pago -->
      <div v-if="bloquearUI && !clientSecret" class="text-center py-4">
        <span class="spinner-border spinner-border-sm me-2"></span>
        {{ $t('pronostico.processingPayment') }}
      </div>

      <div ref="anclaStripe"></div>
      <div v-if="clientSecret && publicKey && !pagoCompletado">
        <component
          :is="usePaymentElement ? 'PaymentStripeElement' : 'PaymentStripe'"
          :key="clientSecret + '-' + idiomaActual"
          :clientSecret="clientSecret"
          :publicKey="publicKey"
          :email="emailLogged"
          :pais="pais"
          :mode="stripeMode"
          :referencia="referenciaPedido"
          :diasGracia="diasGracia"
          :locale="idiomaActual"
          :esSuscripcion="esSuscripcion"
          @cerrar-componente="reenviarCerrar"
        />
        <p v-if="bloquearUI && !pagoCompletado" class="text-muted mt-3" style="text-align:center;">
          {{ $t('pronostico.processingPayment') }}
        </p>
      </div>
    </template>
  </div>

  <!-- Caso B: logueado, CON url (enlace generado) -->
  <div v-else style="max-width:400px;margin: 3rem auto;">
    <p class="mb-2" style="text-align:left;font-size:1.05rem;">
      {{ $t('pronostico.loggedAs') }}
    </p>

    <div class="mb-3 text-start">
      <input
        type="email"
        :value="emailLogged"
        class="form-control"
        readonly
        disabled
        style="background:#f8f9fa; color:#495057;"
      />
    </div>

    <p class="mt-5" style="text-align:left;font-size:1.05rem;">
      {{ $t('pronostico.yourLinkIs') }}
    </p>

    <a :href="mostrarUrl" target="_blank" style="display:block;text-decoration:underline;">
      {{ mostrarUrl }}
    </a>

    <a href="/pedidos">
      <QBtn type="button" variant="tipster" class="float-end mt-4">
        <span class="text-xs">{{ $t('pronostico.goToPanel') }} &nbsp;<i class="fa fa-chevron-right"></i></span>
      </QBtn>
    </a>
  </div>
</template>

<script>
import PaymentStripe from "@/components/PaymentStripe.vue";
import PaymentStripeElement from "@/components/PaymentStripeElement.vue";

export default {
  name: "PronosticoFormLoggedIn",
  components: { PaymentStripe, PaymentStripeElement },
  props: {
    emailLogged: { type: String, default: "" },
    clientSecret: { type: String, default: "" },
    publicKey: { type: String, default: "" },
    bloquearUI: { type: Boolean, default: false },
    pagoCompletado: { type: Boolean, default: false },
    mostrarUrl: { type: String, default: "" },
    producto: { type: Object, default: () => ({}) },
    descuentoOK: { type: Boolean, default: false },
    precioNormalMostrado: { type: Number, default: 0 },
    precioFinalMostrado: { type: Number, default: 0 },
    rateMoneda: { type: Number, default: 1 },
    simboloMoneda: { type: String, default: "€" },
    idiomaActual: { type: String, default: "es" },
    stripeMode: { type: String, default: "payment" },
    referenciaPedido: { type: String, default: "" },
    diasGracia: { type: Number, default: 0 },
    usePaymentElement:  { type: Boolean, default: false },
    pais:               { type: String, default: "" },
    paises:             { type: Array, default: () => [] },
    esSuscripcion:      { type: Boolean, default: false },
  },
  emits: ["cerrar-componente", "pais-seleccionado"],
  data() {
    return {
      paisSeleccionado: '',
    };
  },
  computed: {
    paisesOrdenados() {
      return [...this.paises].sort((a, b) =>
        this.getCountryLabel(a).localeCompare(this.getCountryLabel(b))
      );
    },
  },
  methods: {
    reenviarCerrar(...args) {
      this.$emit('cerrar-componente', ...args);
    },
    onPaisChange() {
      if (this.paisSeleccionado) {
        this.$emit('pais-seleccionado', this.paisSeleccionado);
      }
    },
    getCountryLabel(code) {
      try {
        const value = this.$t(`countries.${code}`);
        if (value && value !== `countries.${code}`) return value;
      } catch (_) { /* noop */ }
      // Fallback: Intl.DisplayNames conoce todos los códigos ISO sin lista manual
      try {
        const names = new Intl.DisplayNames([this.$i18n.locale, 'es'], { type: 'region' });
        return names.of(code) || code;
      } catch (_) { return code; }
    },
  },
  watch: {
    clientSecret(val) {
      if (val) {
        this.$nextTick(() => {
          setTimeout(() => {
            try {
              this.$refs.anclaStripe?.scrollIntoView({ behavior: "smooth", block: "start" });
            } catch (e) { /* noop */ }
          }, 350);
        });
      }
    },
  },
};
</script>

<style scoped>
.trial-info-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(40, 167, 69, 0.1);
  border: 1px solid rgba(40, 167, 69, 0.3);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 0.9rem;
}
.trial-info-icon {
  font-size: 1.2rem;
  flex-shrink: 0;
  margin-top: 1px;
}
.trial-info-sub {
  color: rgba(231, 237, 245, 0.7);
  font-size: 0.82rem;
}
.pais-selector-card {
  background: var(--q-card);
  border: 1px solid var(--q-border-md);
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
}
.pais-selector-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--q-text);
}

@media (max-width: 600px) {
  .loggedin-wrapper {
    margin-top: 1rem !important;
    padding: 0 0.5rem;
  }
}
</style>
